import Vue from 'vue'
import { normalize, schema } from 'normalizr'
import {
  RECEIVE_BUDGETLINES,
  RECEIVING_BUDGETLINE,
  RECEIVE_BUDGETLINE_METADATA,
  RECEIVE_BUDGETLINE_FAILURE,
  SAVING_BUDGETLINE,
  SAVING_BUDGETLINE_FAILURE,
  UPDATING_BUDGETLINE,
  UPDATING_BUDGETLINE_FAILURE,
  DELETE_BUDGETLINE,
  DELETING_BUDGETLINE,
  DELETING_BUDGETLINE_FAILURE,
  CLEAR_STATE
} from '../mutationTypes'
import { checkStatus, getHeaders, parseJSON, buildUrl } from '@/utils/api'
import handleXHRError from '@/utils/helpers/HandleXHRError'
import { receiveMetadata } from '@/store/modules/helpers/mutationHelpers'

const budgetSchema = new schema.Entity('budgetlines')
const budgetListSchema = new schema.Array(budgetSchema)

const state = {
  data: null,
  count: null,
  shown: null,
  next: null,
  previous: null,
  saving: false,
  receivingData: false,
  receiveDataFailure: false,
  updating: { id: '', status: false },
  updateFailure: { id: '', err: '' },
  deleting: false
}

const actions = {
  fetchBudgetlines (store, query) {
    const urlToFetchFrom = () => {
      const instanceUrl = this.getters.getActiveInstance.url
      return buildUrl('budgetlines', Object.keys(query).length ? query : null, instanceUrl)
    }
    const headers = getHeaders(this.getters.getToken)
    this.commit(RECEIVING_BUDGETLINE, true)
    return Vue.http.get(urlToFetchFrom(), { headers })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        this.commit(RECEIVING_BUDGETLINE, false)
        if (r.hasOwnProperty('results')) {
          const { results, next, previous, count } = r
          const { entities, result } = normalize(results, budgetListSchema)
          this.commit(RECEIVE_BUDGETLINES, !result || !result.length ? results : entities.budgetlines)
          this.commit(RECEIVE_BUDGETLINE_METADATA, { next, previous, count, result: result.length })
          return r
        }
      })
      .catch(e => {
        return this.commit(RECEIVE_BUDGETLINE_FAILURE, handleXHRError(e))
      })
  },
  fetchBudgetlineOptions (store, query) {
    const instanceUrl = this.getters.getActiveInstance.url
    this.commit(RECEIVING_BUDGETLINE, true)
    const headers = getHeaders(this.getters.getToken)
    return Vue.http({ url: buildUrl('budgetlines', query, instanceUrl), headers, method: 'options' })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        this.commit(RECEIVING_BUDGETLINE, false)
        if (r.hasOwnProperty('choices')) {
          const { choices } = r
          const { entities, result } = normalize(choices, budgetListSchema)
          this.commit(RECEIVE_BUDGETLINES, entities.budgetlines)
          this.commit(RECEIVE_BUDGETLINE_METADATA, { count: result.length })
          return entities
        }
      })
      .catch(e => {
        return this.commit(RECEIVE_BUDGETLINE_FAILURE, handleXHRError(e))
      })
  },
  saveBudgetline (store, payload) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(SAVING_BUDGETLINE, true)
    this.commit(SAVING_BUDGETLINE_FAILURE, null)
    if (payload) {
      return Vue.http.post(buildUrl('budgetlines', null, instanceUrl), payload, { headers })
        .then(checkStatus)
        .then(parseJSON)
        .then(r => {
          if (r) {
            this.commit(SAVING_BUDGETLINE, false)
            const { entities, result } = normalize(r, budgetSchema)
            this.commit(RECEIVE_BUDGETLINES, !result ? r : entities.budgetlines)
            this.commit(RECEIVE_BUDGETLINE_METADATA)
            return r
          }
        })
        .catch(e => {
          return this.commit(SAVING_BUDGETLINE_FAILURE, handleXHRError(e))
        })
    } else {
      return this.commit(SAVING_BUDGETLINE_FAILURE, 'Error saving budget')
    }
  },
  updateBudgetline (store, payload) {
    const { id, data } = payload
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(UPDATING_BUDGETLINE, { id: id, status: true })
    return Vue.http.patch(buildUrl(`budgetlines/${id}`, null, instanceUrl), data, { headers })
      .then(r => {
        this.commit(UPDATING_BUDGETLINE, { id: id, status: false })
        if (r && r.status === 200) {
          const { entities, result } = normalize(r.body, budgetSchema)
          this.commit(RECEIVE_BUDGETLINES, !result ? r.body : entities.budgetlines)
          return r.body
        } else {
          this.commit(RECEIVE_BUDGETLINE_FAILURE, r ? r.statusText : 'Failed to update budget line')
        }
      })
      .catch(e => {
        this.commit(UPDATING_BUDGETLINE, null)
        this.commit(UPDATING_BUDGETLINE_FAILURE, { id, err: handleXHRError(e) })
      })
  },
  deleteBudgetline (store, payload) {
    const instanceUrl = this.getters.getActiveInstance.url
    if (payload) {
      this.commit(DELETING_BUDGETLINE, true)
      return Vue.http.delete(buildUrl(`budgetlines/${payload}`, null, instanceUrl), { headers: getHeaders(this.getters.getToken) })
        .then(checkStatus)
        .then(() => {
          this.commit(DELETE_BUDGETLINE, payload)
          this.commit(DELETING_BUDGETLINE, false)
          return payload
        })
    } else {
      return this.commit(DELETING_BUDGETLINE, false)
    }
  }
}

const getters = {
  getBudgetlines: (state) => {
    return state.data ? Object.values(state.data).map(v => Object.assign({}, v)) : []
  },
  getBudgetlineMetadata: (state) => {
    const { shown, count } = state
    if (!shown) return { count: 0, shown: 0 }
    return { count, shown }
  },
  getBudgetlineById: (state) => (id) => {
    if (state.data) return state.data[id]
  },
  getBudgetlineFetchStatus: (state) => {
    return state.receivingData
  },
  getBudgetlineSaveStatus: (state) => {
    return state.saving
  },
  getBudgetlineUpdateStatus: (state) => {
    return state.updating
  },
  getBudgetlineDeleteStatus: (state) => {
    return state.deleting
  }
}

const mutations = {
  [RECEIVE_BUDGETLINES] (state, results) {
    if (!results || !Object.keys(results).length) return state.data
    if (state.data && Object.keys(state.data).length) {
      state.data = Object.assign({}, state.data, results)
    } else {
      state.data = results
    }
  },
  [RECEIVING_BUDGETLINE] (state, payload) {
    state.receivingData = payload
  },
  [RECEIVE_BUDGETLINE_FAILURE] (state, error) {
    state.receiveDataFailure = error
  },
  [SAVING_BUDGETLINE] (state, payload) {
    state.saving = { status: payload, err: null }
  },
  [SAVING_BUDGETLINE_FAILURE] (state, payload) {
    state.saving = { status: false, err: payload }
  },
  [UPDATING_BUDGETLINE] (state, payload) {
    state.updating = payload
  },
  [UPDATING_BUDGETLINE_FAILURE] (state, payload) {
    const { id, err } = payload
    state.updateFailure = { id, err }
  },
  [DELETE_BUDGETLINE] (state, payload) {
    let tempData = Object.assign({}, state.data)
    for (let t in tempData) {
      if (!tempData.hasOwnProperty(t)) continue
      if (tempData[t].id === payload) {
        delete tempData[t]
        state.count = state.count - 1
      }
    }
    state.data = Object.assign({}, tempData)
  },
  [DELETING_BUDGETLINE] (state, payload) {
    state.deleting = payload
  },
  [DELETING_BUDGETLINE_FAILURE] (state, payload) {
    const { id, err } = payload
    state.deleting = { status: false, id, err }
  },
  [RECEIVE_BUDGETLINE_METADATA] (state, payload) {
    receiveMetadata(state, payload)
  },
  [CLEAR_STATE] (state) {
    state.data = null
    state.count = null
    state.shown = null
    state.next = null
    state.previous = null
    state.saving = false
    state.receivingData = false
    state.receiveDataFailure = false
    state.updating = { id: '', status: false }
    state.updateFailure = { id: '', err: '' }
    state.deleting = false
  }
}

export default {
  state,
  actions,
  getters,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/modules/budgetline.js